import type { NostrEvent } from '@nostrify/nostrify';

/** Addressable kind for an owner-private budget entry (NIP-44 encrypted to self). */
export const BUDGET_ENTRY_KIND = 34529;
/** Regular kind for a budget entry shared with another user (NIP-44 encrypted to recipient). */
export const SHARED_ENTRY_KIND = 1431;
/** NIP-78 application-data identifier for the private contacts list. */
export const CONTACTS_D_TAG = 'notebudget/contacts';
/** NIP-78 application-data identifier for the private shared-balance partners list. */
export const PARTNERS_D_TAG = 'budgetstr/partners';
/** NIP-78 application-data identifier for the private piggy bank accounts list. */
export const PIGGYBANK_D_TAG = 'budgetstr/piggybank';
/** NIP-78 application-data identifier for the private category budgets list. */
export const CATEGORIES_D_TAG = 'budgetstr/categories';
/** NIP-78 application-data identifier for the private cash holdings list. */
export const CASH_D_TAG = 'budgetstr/cash';
/** NIP-78 application-data identifier for the private investments list. */
export const INVESTMENTS_D_TAG = 'budgetstr/investments';
/** NIP-78 application-data identifier for the private debts list. */
export const DEBTS_D_TAG = 'budgetstr/debts';

export type EntryType = 'expense' | 'income';

/** How often an entry repeats. `none` is a one-off entry. */
export type Recurrence = 'none' | 'daily' | 'weekly' | 'monthly' | 'yearly';

/** Recurrence options for the picker, in display order. */
export const RECURRENCES: { value: Recurrence; label: string; short: string }[] = [
  { value: 'none', label: 'One-time', short: 'Once' },
  { value: 'daily', label: 'Daily', short: 'Daily' },
  { value: 'weekly', label: 'Weekly', short: 'Weekly' },
  { value: 'monthly', label: 'Monthly', short: 'Monthly' },
  { value: 'yearly', label: 'Yearly', short: 'Yearly' },
];

/** Human-readable label for a recurrence value. */
export function recurrenceLabel(recurrence: Recurrence): string {
  return RECURRENCES.find((r) => r.value === recurrence)?.short ?? 'Once';
}

/** The decrypted JSON payload stored inside an entry's content. */
export interface BudgetEntryPayload {
  title: string;
  amount: number;
  currency: string;
  type: EntryType;
  category: string;
  note: string;
  date: string; // ISO date (yyyy-mm-dd)
  createdAt: number; // unix seconds
  /** How often this entry repeats. Defaults to 'none' for legacy entries. */
  recurrence?: Recurrence;
}

/** A decrypted budget entry owned by the current user. */
export interface BudgetEntry extends BudgetEntryPayload {
  id: string; // the `d` tag identifier
  event: NostrEvent;
}

/** Extra fields present in a shared entry payload. */
export interface SharedEntryPayload extends BudgetEntryPayload {
  entryId: string;
  sharedBy: string; // npub of sharer
}

/** A decrypted entry that was shared with the current user. */
export interface SharedEntry extends SharedEntryPayload {
  eventId: string;
  sharerPubkey: string; // hex pubkey of the author of the shared event
  event: NostrEvent;
}

export interface Contact {
  pubkey: string; // hex
  name?: string;
}

/**
 * A shared-balance partner (e.g. a spouse). When you add a partner, every
 * entry you create is automatically shared with them, and their entries are
 * pooled into your combined balance, income, and expense totals.
 */
export interface Partner {
  pubkey: string; // hex
  name?: string;
}

/** Known piggy bank provider presets for quick setup. */
export const PIGGYBANK_PROVIDERS = [
  { id: 'river', label: 'River', placeholder: 'username@river.com' },
  { id: 'strike', label: 'Strike', placeholder: 'username@strike.me' },
  { id: 'cashapp', label: 'Cash App', placeholder: '$cashtag@cash.app' },
  { id: 'albyhub', label: 'Alby Hub', placeholder: 'username@getalby.com' },
  { id: 'custom', label: 'Other', placeholder: 'username@domain.com' },
] as const;

/** Recognized investment categories. */
export const INVESTMENT_TYPES = [
  { id: 'stocks', label: 'Stocks' },
  { id: 'crypto', label: 'Crypto' },
  { id: 'retirement', label: 'Retirement' },
  { id: 'real_estate', label: 'Real estate' },
  { id: 'other', label: 'Other' },
] as const;

export type InvestmentType = typeof INVESTMENT_TYPES[number]['id'];

/**
 * A user-tracked investment balance (a brokerage account, crypto wallet,
 * 401k, real estate, etc.). `currentValue` is the present market value;
 * `costBasis` is optional — when provided, the dashboard shows
 * unrealized gain/loss.
 */
export interface Investment {
  /** UUID. */
  id: string;
  /** Human-friendly label (e.g. "Vanguard IRA", "Coinbase BTC"). */
  label: string;
  /** Current market value. */
  currentValue: number;
  /** Currency code. */
  currency: string;
  /** Investment category. */
  type: InvestmentType;
  /** Optional cost basis (what you paid). When set, P/L is shown. */
  costBasis?: number;
  /** Optional free-form note. */
  note?: string;
}

/** Recognized debt categories. */
export const DEBT_TYPES = [
  { id: 'mortgage', label: 'Mortgage' },
  { id: 'credit_card', label: 'Credit card' },
  { id: 'student_loan', label: 'Student loan' },
  { id: 'auto_loan', label: 'Auto loan' },
  { id: 'personal_loan', label: 'Personal loan' },
  { id: 'medical', label: 'Medical' },
  { id: 'other', label: 'Other' },
] as const;

export type DebtType = typeof DEBT_TYPES[number]['id'];

/**
 * A user-tracked debt balance (mortgage, credit card, student loan, etc.).
 * `balance` is the outstanding amount owed; optional `apr` and
 * `minPayment` help the user see total monthly obligations.
 */
export interface Debt {
  /** UUID. */
  id: string;
  /** Human-friendly label (e.g. "Chase Visa", "Mortgage"). */
  label: string;
  /** Outstanding balance owed. */
  balance: number;
  /** Currency code. */
  currency: string;
  /** Debt category. */
  type: DebtType;
  /** Optional APR as a percentage (e.g. 18.99). */
  apr?: number;
  /** Optional minimum monthly payment. */
  minPayment?: number;
  /** Optional free-form note. */
  note?: string;
}

/**
 * A cash balance the user holds on hand — could be a physical wallet,
 * a digital app balance (Cash App, Venmo, etc.), or a stash. The `paper`
 * flag distinguishes physical paper money from digital cash equivalents.
 */
export interface CashHolding {
  /** UUID. */
  id: string;
  /** Human-friendly label (e.g. "Wallet", "Safe", "Cash App"). */
  label: string;
  /** Amount on hand. */
  amount: number;
  /** Currency code. */
  currency: string;
  /** True if this is physical paper money / coins, false if digital cash. */
  paper: boolean;
}

/** A saved lightning address the user sends monthly savings to. */
export interface PiggyBankAccount {
  /** UUID. */
  id: string;
  /** Human-friendly label (e.g. "River savings"). */
  label: string;
  /** Lightning address (user@domain). */
  address: string;
  /** Optional provider key for icon/display. */
  provider?: string;
}

/** Common currencies for the picker. */
export const CURRENCIES = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'BTC', 'SATS'] as const;

/** Default categories used when the user hasn't customized their list. */
export const DEFAULT_CATEGORIES = [
  'Food',
  'Housing',
  'Transport',
  'Utilities',
  'Health',
  'Entertainment',
  'Shopping',
  'Salary',
  'Gift',
  'Other',
] as const;

/**
 * A user-defined category with an optional monthly budget.
 * When `budget` is > 0, expenses in this category are tracked against it.
 */
export interface CategoryBudget {
  /** Category name (unique within the list). */
  name: string;
  /** Monthly budget amount (0 = no budget set). */
  budget: number;
  /** Currency for the budget (defaults to the user's dominant currency). */
  currency: string;
}

/** Format a money amount for display. */
export function formatAmount(amount: number, currency: string): string {
  if (currency === 'SATS') {
    return `${Math.round(amount).toLocaleString()} sats`;
  }
  if (currency === 'BTC') {
    return `₿${amount.toLocaleString(undefined, { maximumFractionDigits: 8 })}`;
  }
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
    }).format(amount);
  } catch {
    return `${amount.toLocaleString()} ${currency}`;
  }
}

/** Generate a UUID for entry identifiers. */
export function newEntryId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// ---------------------------------------------------------------------------
// Monthly budget math
// ---------------------------------------------------------------------------

/** The minimal shape needed to compute a monthly contribution. */
export interface RecurringLike {
  amount: number;
  date: string; // ISO start/anchor date (yyyy-mm-dd)
  recurrence?: Recurrence;
}

/** Number of days in the given month (0-indexed month). */
function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

/** Parse a `yyyy-mm-dd` string into a local Date (midnight). Returns null if invalid. */
export function parseEntryDate(date: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m) {
    const d = new Date(date);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * How much a (possibly recurring) entry contributes to the given month.
 *
 * Rules:
 * - `none`: counts only if the entry's date falls within the month.
 * - `daily`: amount × number of days in the month (only once the anchor date
 *   has started, i.e. not for months before the entry began).
 * - `weekly`: amount × number of weekly occurrences that land in the month.
 * - `monthly`: amount once per month (once the anchor month has begun).
 * - `yearly`: amount only in the anniversary month (its "due date"); 0 in all
 *   other months. Yearly items are otherwise handled via the savings field.
 *
 * `year` is the full year, `month` is 0-indexed (0 = January).
 */
export function monthlyContribution(
  entry: RecurringLike,
  year: number,
  month: number,
): number {
  const recurrence = entry.recurrence ?? 'none';
  const start = parseEntryDate(entry.date);
  if (!start) return 0;

  const monthStart = new Date(year, month, 1);
  const monthEnd = new Date(year, month, daysInMonth(year, month));

  // Nothing recurs before it begins.
  const startsAfterMonth = start > monthEnd;

  switch (recurrence) {
    case 'none':
      return start >= monthStart && start <= monthEnd ? entry.amount : 0;

    case 'daily': {
      if (startsAfterMonth) return 0;
      // Count days in the month on/after the anchor date.
      const from = start > monthStart ? start : monthStart;
      const days = Math.floor((monthEnd.getTime() - from.getTime()) / 86_400_000) + 1;
      return entry.amount * Math.max(0, days);
    }

    case 'weekly': {
      if (startsAfterMonth) return 0;
      // Count weekly anniversaries (every 7 days from the anchor) in the month.
      let occurrences = 0;
      const cursor = new Date(start);
      // Fast-forward to the first occurrence within or after the month start.
      if (cursor < monthStart) {
        const weeks = Math.ceil((monthStart.getTime() - cursor.getTime()) / (7 * 86_400_000));
        cursor.setDate(cursor.getDate() + weeks * 7);
      }
      while (cursor <= monthEnd) {
        occurrences++;
        cursor.setDate(cursor.getDate() + 7);
      }
      return entry.amount * occurrences;
    }

    case 'monthly':
      return startsAfterMonth ? 0 : entry.amount;

    case 'yearly': {
      // Only counts in its anniversary (due) month, and not before it began.
      if (startsAfterMonth) return 0;
      return start.getMonth() === month ? entry.amount : 0;
    }

    default:
      return 0;
  }
}

/** Type guard for a decoded entry payload. */
export function isBudgetEntryPayload(value: unknown): value is BudgetEntryPayload {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.title === 'string' &&
    typeof v.amount === 'number' &&
    typeof v.currency === 'string' &&
    (v.type === 'expense' || v.type === 'income') &&
    typeof v.date === 'string'
  );
}
